import { useState } from 'react';
import { Dropdown } from '@heroui/react';
import { IconDotsVerticalFilled } from '@tabler/icons-react';
import type { TableElement as TableElementType } from '@/components/canvas/document/publicTypes';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { SelectionChromeFrame } from '@/components/canvas/selection/SelectionChromeFrame';
import { SelectionHandleVisual } from '@/components/canvas/selection/SelectionHandleVisual';
import { handleChromeWorldMetrics } from '@/components/canvas/selection/selectionChrome';
import {
  TABLE_ROW_GRIP_HEIGHT_PX,
  TABLE_ROW_GRIP_WIDTH_PX,
} from '@/components/canvas/elements/table/constants';
import { patchMergeCellRange } from '@/components/canvas/elements/table/utils/tableStructure';

export type TableCellRange = { r0: number; c0: number; r1: number; c1: number };

export type TableOverlaysProps = {
  element: TableElementType;
  rows: number;
  cols: number;
  rowHeights: number[];
  colWidths: number[];
  zoom: number;
  editingCell: { row: number; col: number } | null;
  isCellRangeSelection: boolean;
  cellRange: TableCellRange | null;
  colHighlight: boolean;
  rowHighlight: boolean;
  selectedTableColumn: { elementId: string; col: number } | null | undefined;
  selectedTableRow: { elementId: string; row: number } | null | undefined;
  setCellRangeDrag: (drag: 'tl' | 'br' | null) => void;
};

const cellMenuGripClass =
  'flex size-full min-h-0 min-w-0 items-center justify-center rounded-sm bg-accent text-accent-foreground outline-none';

export function TableOverlays({
  element,
  rows: _rows,
  cols: _cols,
  rowHeights,
  colWidths,
  zoom,
  editingCell,
  cellRange,
  colHighlight,
  rowHighlight,
  selectedTableColumn,
  selectedTableRow,
  setCellRangeDrag,
}: TableOverlaysProps) {
  const { updateElement } = useCanvas();
  const [cellMenuOpen, setCellMenuOpen] = useState(false);

  const tableWidth = colWidths.reduce((a, b) => a + b, 0);
  const tableHeight = rowHeights.reduce((a, b) => a + b, 0);
  const { hitSize: cellHandleSize, hitHalf: handleHalf } = handleChromeWorldMetrics(zoom);

  const isMultiCell =
    cellRange != null && (cellRange.r0 !== cellRange.r1 || cellRange.c0 !== cellRange.c1);

  const handleMergeCells = () => {
    if (!cellRange || !isMultiCell) return;
    const patch = patchMergeCellRange(
      element,
      cellRange.r0,
      cellRange.c0,
      cellRange.r1,
      cellRange.c1,
    );
    if (patch) updateElement(element.id, patch);
    setCellMenuOpen(false);
  };

  return (
    <div
      className="pointer-events-none absolute left-0 top-0"
      style={{ width: tableWidth, height: tableHeight, zIndex: 20 }}
      aria-hidden
    >
      {/* 单元格选区 */}
      {!editingCell && cellRange && (() => {
        const selLeft = colWidths.slice(0, cellRange.c0).reduce((a, b) => a + b, 0);
        const selTop = rowHeights.slice(0, cellRange.r0).reduce((a, b) => a + b, 0);
        const selW = colWidths.slice(cellRange.c0, cellRange.c1 + 1).reduce((a, b) => a + b, 0);
        const selH = rowHeights.slice(cellRange.r0, cellRange.r1 + 1).reduce((a, b) => a + b, 0);
        const menuGripTop = selTop + selH / 2 - TABLE_ROW_GRIP_HEIGHT_PX / 2;
        const menuGripLeft = selLeft + selW - TABLE_ROW_GRIP_WIDTH_PX / 2;
        return (
          <>
            <SelectionChromeFrame
              left={selLeft}
              top={selTop}
              width={selW}
              height={selH}
              zoom={zoom}
            />
            <div
              className="pointer-events-auto absolute flex items-center justify-center cursor-nwse-resize"
              style={{
                left: selLeft - handleHalf,
                top: selTop - handleHalf,
                width: cellHandleSize,
                height: cellHandleSize,
                zIndex: 1,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCellMenuOpen(false);
                setCellRangeDrag('tl');
              }}
            >
              <SelectionHandleVisual zoom={zoom} className="pointer-events-none shrink-0" />
            </div>
            <div
              className="pointer-events-auto absolute flex items-center justify-center cursor-nwse-resize"
              style={{
                left: selLeft + selW - handleHalf,
                top: selTop + selH - handleHalf,
                width: cellHandleSize,
                height: cellHandleSize,
                zIndex: 1,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCellMenuOpen(false);
                setCellRangeDrag('br');
              }}
            >
              <SelectionHandleVisual zoom={zoom} className="pointer-events-none shrink-0" />
            </div>
            {/* 选区右侧菜单握把（样式对齐行列轴握把） */}
            <div
              className="pointer-events-auto absolute overflow-hidden rounded-sm shadow-sm"
              style={{
                left: menuGripLeft,
                top: menuGripTop,
                width: TABLE_ROW_GRIP_WIDTH_PX,
                height: TABLE_ROW_GRIP_HEIGHT_PX,
                zIndex: 3,
              }}
            >
              <button
                type="button"
                aria-label="单元格菜单"
                className={`${cellMenuGripClass} cursor-pointer`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCellMenuOpen(true);
                }}
              >
                <IconDotsVerticalFilled size={16} />
              </button>
              <Dropdown
                isOpen={cellMenuOpen}
                onOpenChange={setCellMenuOpen}
                className="absolute inset-0 size-full"
              >
                <Dropdown.Trigger
                  className="pointer-events-none absolute inset-0 min-h-0 min-w-0 border-0 bg-transparent p-0 opacity-0"
                  aria-hidden
                >
                  <span className="sr-only">单元格菜单</span>
                </Dropdown.Trigger>
                <Dropdown.Popover className="min-w-[160px]" placement="right" offset={6}>
                  <Dropdown.Menu
                    onAction={(key) => {
                      if (key === 'merge') handleMergeCells();
                    }}
                  >
                    {/* 须补充：单格选中时的菜单项 */}
                    {isMultiCell ? (
                      <Dropdown.Item id="merge" textValue="合并单元格">
                        合并单元格
                      </Dropdown.Item>
                    ) : null}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown>
            </div>
          </>
        );
      })()}

      {/* 列/行选中高亮 */}
      {!editingCell && colHighlight && selectedTableColumn && (
        <SelectionChromeFrame
          left={colWidths.slice(0, selectedTableColumn.col).reduce((a, b) => a + b, 0)}
          top={0}
          width={colWidths[selectedTableColumn.col]}
          height={tableHeight}
          zoom={zoom}
        />
      )}
      {!editingCell && rowHighlight && selectedTableRow && (
        <SelectionChromeFrame
          left={0}
          top={rowHeights.slice(0, selectedTableRow.row).reduce((a, b) => a + b, 0)}
          width={tableWidth}
          height={rowHeights[selectedTableRow.row]}
          zoom={zoom}
        />
      )}
    </div>
  );
}
